import { apiRequest, resolveApiUrl } from "@/api/client";
import { readLocalImage } from "@/api/local-images";
import {
  captureDesktopChatAuthScopeGuard,
  getDesktopChatAuthScope,
  subscribeDesktopChatAuthScope,
} from "@/desktop/desktop-chat-auth-scope";

export type ResolvedAttachmentMedia = { url: string; expiresAt: number };
type Access = {
  id: string;
  storage_kind: "local" | "cloud";
  url: string | null;
  expires_at: string | number | null;
};
const cache = new Map<
  string,
  { promise: Promise<ResolvedAttachmentMedia>; bytes: number }
>();
const MAX_CACHE_BYTES = 64 * 1024 * 1024;
function trimCache() {
  let bytes = Array.from(cache.values()).reduce(
    (sum, entry) => sum + entry.bytes,
    0,
  );
  for (const [key, entry] of cache) {
    if (cache.size <= 64 && bytes <= MAX_CACHE_BYTES) break;
    cache.delete(key);
    bytes -= entry.bytes;
  }
}

/** Only this backend's upload URLs are credentialed. Tokens are never durable IDs. */
export function uploadAssetPath(value: string): string | null {
  let url: URL;
  try {
    url = new URL(value, new URL(resolveApiUrl("/"), window.location.href));
  } catch {
    return null;
  }
  const base = new URL(resolveApiUrl("/"), window.location.href);
  if (url.origin !== base.origin) return null;
  return /^\/api\/v1\/uploads\/[^/]+\/(image|file)\/$/.test(url.pathname) ||
    url.pathname.startsWith("/media/")
    ? url.pathname
    : null;
}

export function canonicalAttachmentUrl(value: string): string {
  return uploadAssetPath(value) ?? value;
}

/** Strip temporary access grants before saving JSON image and note references. */
export function canonicalizeAttachmentReferences<T>(value: T): T {
  if (typeof value === "string") return canonicalAttachmentUrl(value) as T;
  if (Array.isArray(value))
    return value.map(canonicalizeAttachmentReferences) as T;
  if (value && typeof value === "object")
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => [
        key,
        canonicalizeAttachmentReferences(child),
      ]),
    ) as T;
  return value;
}

function currentKey(path: string): string {
  const scope = getDesktopChatAuthScope();
  if (!scope) throw new Error("Sign in to view this attachment.");
  return `${scope.accountId}\n${scope.epoch}\n${path}`;
}
subscribeDesktopChatAuthScope(() => cache.clear());

export async function resolveAttachmentMedia(
  value: string,
  options: { refresh?: boolean } = {},
): Promise<ResolvedAttachmentMedia> {
  const path = uploadAssetPath(value);
  if (!path) return { url: resolveApiUrl(value), expiresAt: Infinity };
  const key = currentKey(path);
  const guard = captureDesktopChatAuthScopeGuard();
  const cached = cache.get(key);
  if (cached && !options.refresh) {
    const result = await cached.promise;
    if (!guard()) throw new Error("The signed-in account changed.");
    if (result.expiresAt > Date.now() + 30_000) {
      cache.delete(key);
      cache.set(key, cached);
      return result;
    }
  }
  const pending = (async () => {
    const match = path.match(/^\/api\/v1\/uploads\/([^/]+)\//);
    if (match) {
      try {
        const url = await readLocalImage(match[1]!);
        if (!guard()) throw new Error("The signed-in account changed.");
        return { url, expiresAt: Infinity };
      } catch {
        if (!guard()) throw new Error("The signed-in account changed.");
      }
    }
    const endpoint = match
      ? `/api/v1/uploads/${match[1]}/access/`
      : `/api/v1/uploads/access/?url=${encodeURIComponent(path)}`;
    const response = await apiRequest<{ data: Access }>(endpoint, {
      silent: true,
      requestGuard: guard,
    });
    if (!guard()) throw new Error("The signed-in account changed.");
    const access = response.data;
    if (access.storage_kind === "local") {
      const url = await readLocalImage(access.id);
      if (!guard()) throw new Error("The signed-in account changed.");
      return { url, expiresAt: Infinity };
    }
    if (!access.url) throw new Error("The attachment is unavailable.");
    const expiresAt =
      typeof access.expires_at === "number"
        ? access.expires_at * 1000
        : Date.parse(access.expires_at ?? "");
    return {
      url: resolveApiUrl(access.url),
      expiresAt: Number.isFinite(expiresAt)
        ? expiresAt
        : Date.now() + 10 * 60_000,
    };
  })();
  const entry = { promise: pending, bytes: 0 };
  cache.set(key, entry);
  trimCache();
  try {
    const result = await pending;
    if (!guard()) throw new Error("The signed-in account changed.");
    entry.bytes = result.url.length * 2;
    trimCache();
    return result;
  } catch (error) {
    if (cache.get(key) === entry) cache.delete(key);
    throw error;
  }
}
